'use strict';

import OrganizationListItemModule from './organizationListItem.ctrl.js';

class OrganizationsListController {
  constructor(
    $rootScope,
    $scope,
    $filter,
    $state,
    $interval,
    utils,
    OrganizationService,
    PermissionsService,
    ToastsService
  ) {

    // region Define fields

    const filterKey = 'Filter-Organizations';
    const $t = $filter('translate');
    const ORGANIZATIONS_LOCAL_STORAGE_KEY = 'Organizations-All';
    const CURRENT_ORG_LOCAL_STORAGE_KEY = 'Organizations-Current';
    const SELECTED_ORG_LOCAL_STORAGE_KEY = 'Selected-Org';

    $scope.searchVal = '';
    $scope.selectedOrg = utils.getStorageItem(CURRENT_ORG_LOCAL_STORAGE_KEY);
    $scope.canICreateOrganization = PermissionsService.canICreateOrganization();
    $scope.relatedOrganizations = [];

    // endregion

    // region Put on scope only the stuff we really need there
    angular.extend($scope, {
      deleteOrg: deleteOrg,
      editOrg: editOrg,
      chooseOrg: chooseOrg,
      onSearch: onSearch,
      toggleSearchById: toggleSearchById,
    });
    // endregion

    // Run initialization routine
    init();

    // region Methods

    function init() {
      // Init filter
      initFilterFromCache();
      initOrgsFromCache();

      // Get data from server
      getOrganizations();

      // Update org list every 10 seconds
      const updateOrgsInterval = $interval(() => {
        if (!$scope.loading) {
          getOrganizations(true);
        }
      }, 10 * 1000);

      $scope.$on('$destroy', () => {
        $interval.cancel(updateOrgsInterval);
      });
    }

    function initFilterFromCache() {
      $scope.filter = {};
      const params = utils.getStorageItem(filterKey);
      if (params) {
        $scope.filter.searchOrgString = params.searchOrgString || '';
        $scope.filter.searchById = params.searchById || false;
      }
    }

    function initOrgsFromCache() {
      const originalOrgs = utils.getStorageItem(ORGANIZATIONS_LOCAL_STORAGE_KEY);
      if (originalOrgs && originalOrgs.length > 0) {
        processOrganizations(originalOrgs);
      }
    }

    function saveFilter() {
      utils.setStorageItem(filterKey, {
        searchOrgString: $scope.filter.searchOrgString,
        searchById: !!$scope.filter.searchById,
      });
    }

    function onSearch() {
      saveFilter();
      filterOrganizations();
    }

    /**
     * Redirects user to the Edit Organization UI.
     * @param {Object} org
     */
    function editOrg(org) {
      let selectedOrg = angular.copy($scope.originalOrgs.find(o => o.id === org.id));
      utils.setStorageItem(SELECTED_ORG_LOCAL_STORAGE_KEY, selectedOrg);
      $state.go('main.organizations.edit');
    }

    /**
     * Actually deletes organization, shows result info and refreshes the orgs list.
     * @param {number} orgId
     */
    function deleteOrgInternal(orgId) {
      return OrganizationService.deleteOrganization(orgId)
        .then(data => {
          ToastsService.success(null, $t('common.delete-organization-success'));
          if (orgId === $scope.selectedOrg.id) {
            $scope.selectedOrg = null;
            processOrganizations($scope.originalOrgs);
            utils.removeStorageItem(CURRENT_ORG_LOCAL_STORAGE_KEY);
            $scope.$emit('showChooseOrganizationsDialogEvent');
          } else {
            getOrganizations();
          }
        })
        .catch(err => {
          $scope.loading = false;
          utils.handleApiError(err);
        });
    }

    /**
     * Brings up a modal confirmation dialog and then triggers org delete action.
     * @param {Object} org
     */
    function deleteOrg(org) {
      if (!org.canDelete) {
        return;
      }

      $('#delete-organization-dialog')
        .modal({
          closable: false,
          detachable: true,
          onApprove: function() {
            deleteOrgInternal(org.id);
          }
        }).modal('show');
    }

    function chooseOrg(org) {
      if (org.id === $scope.selectedOrg.id) {
        if (!org.canEdit) {
          return ToastsService.error($t('orgs.wrongPermissions.title'), $t('orgs.wrongPermissions.desc'));
        }
        editOrg(org);
      } else {
        $scope.selectedOrg = org;
        // Choose simple one-layer org to save into localStorage
        let selectedOrg = angular.copy($scope.originalOrgs.find(o => o.id === org.id));
        $rootScope.$emit('chooseOrganizationEvent', selectedOrg);

        processOrganizations($scope.originalOrgs);
        // Scroll to top
        $(document).scrollTop(0);
      }
    }

    /**
     * Gets organizations from server using Webcore
     */
    function getOrganizations(silent) {
      if (!silent) {
        $scope.loading = true;
      }

      if (new Date().getTime() - OrganizationService.getOrganizations.getLastCacheDate() > 1000) {
        OrganizationService.getOrganizations.clearEntireCache();
      }

      return OrganizationService.getOrganizations()
        .then(data => {
          processOrganizations(angular.copy(data.organizations));
          $scope.loading = false;
        })
        .then(() => {
          utils.setStorageItem(ORGANIZATIONS_LOCAL_STORAGE_KEY, $scope.originalOrgs);
        })
        .catch(err => {
          $scope.loading = false;
          utils.handleApiError(err);
        });
    }

    function processOrganizations(organizations) {
      if (!organizations || organizations.length === 0) {
        $scope.organizations = [];
        return;
      }

      $scope.originalOrgs = angular.copy(organizations);
      organizations = organizations
        .filter(org => PermissionsService.canIViewOrganization(org));

      // First, we create a Map so that we can quickly select an organization by its id.
      const orgMap = new Map();
      organizations.forEach(org => {
        org.children = [];
        org.name = org.name || '';
        org.address = utils.generateAddress(org);
        org.canEdit = PermissionsService.canIEditOrganization(org);
        org.canDelete = PermissionsService.canIDeleteOrganization(org);
        orgMap.set(org.id, org);
      });

      // Construct children array for every org
      const rootOrgs = [];
      organizations.forEach(org => {
        if (org.parentId !== null && orgMap.has(org.parentId)) {
          const parentOrg = orgMap.get(org.parentId);
          parentOrg.children.push(org);
          org.parent = parentOrg;
        } else {
          rootOrgs.push(org);
        }
      });

      // Sort organizations by name
      const sortOrgFunc = (a, b) => a.name.localeCompare(b.name);
      rootOrgs.sort(sortOrgFunc);
      organizations.forEach(org => {
        org.children.sort(sortOrgFunc);
      });

      // Flatten tree back to list
      const sortedList = [];
      const flatten = (org, level) => {
        org.level = level;
        sortedList.push(org);
        org.children.forEach(child => flatten(child, level + 1));
      };
      rootOrgs.forEach(org => flatten(org, 0));

      $scope.organizations = sortedList;

      // Calculate related orgs
      if (!!$scope.selectedOrg && $scope.organizations.some(o => o.id === $scope.selectedOrg.id)) {
        $scope.selectedOrg = $scope.organizations.find(o => o.id === $scope.selectedOrg.id);

        // First we should find top-level parent of the current org
        let parent = $scope.selectedOrg;
        while (!!parent.parent) {
          parent = parent.parent;
        }

        // And then collect child orgs from all levels. That would be related orgs (except the current one)
        const collectChildren = (org, acc) => {
          acc.push(org);
          org.children.forEach(c => collectChildren(c, acc));
          return acc;
        };
        $scope.relatedOrganizations = collectChildren(parent, [])
          .filter(o => o.id !== $scope.selectedOrg.id);
      } else {
        $scope.selectedOrg = undefined;
        $scope.relatedOrganizations = [];
      }

      // Remove current org and related orgs from the list
      $scope.organizations = $scope.organizations
        .filter(o => o.id !== $scope.selectedOrg?.id)
        .filter(o => !$scope.relatedOrganizations.includes(o));

      filterOrganizations();
    }

    /**
     * Filters organizations
     */
    function filterOrganizations() {
      const searchTerm = ($scope.filter.searchOrgString || '').trim().toLowerCase();

      if (!searchTerm) {
        $scope.organizationFiltered = ($scope.organizations || []).slice();
        $scope.relatedOrganizationsFiltered = ($scope.relatedOrganizations || []).slice();
      } else {
        const searchPredicate = org => {
          if ($scope.filter.searchById) {
            return org.id.toString() === searchTerm;
          }
          return (!!org.name && org.name.toLowerCase().includes(searchTerm)) || org.id.toString() === searchTerm;
        }
        $scope.organizationFiltered = ($scope.organizations || []).filter(searchPredicate);
        $scope.relatedOrganizationsFiltered = ($scope.relatedOrganizations || []).filter(searchPredicate);
      }
    }

    function toggleSearchById() {
      $scope.filter.searchById = !$scope.filter.searchById;
      onSearch();
    }

    // endregion
  }

}

export default angular
  .module('main.organizationsList.controller', [
    OrganizationListItemModule
  ])
  .controller('OrganizationsListController', OrganizationsListController)
  .name;
